// ─────────────────────────────────────────────
// constants.js
// Shared constants for LoadDash / AtlasRouteEQ
// License: MIT (your original IP)
// ─────────────────────────────────────────────

// Canonical LEU numbers live in src/leu/canonical.js (dependency-free).
// constants.js re-exports the route budget and keeps backward-compatible aliases.
import { ROUTE_LEU_BUDGET } from "./leu/canonical";

// Brand
export const BRAND_NAME = "LoadDash";

// Returning-customer one-tap rebook (findings S1-S6, S9, S10).
// Single kill-switch for the actionable rebook CTA + prefill + date steering in
// BookingFlow. Flip to false to instantly revert to the passive-banner behavior.
export const FEATURE_REBOOK_ACTIONABLE = true;

// Brand colors
export const NAVY  = "#0a1628";
export const NAVY2 = "#012340";
export const BLUE  = "#3D90D9";
export const BLUE2 = "#3D90D9";
export const STEEL = "#607d9f";
export const MUTED = "#8fa5be";
export const BORDER= "#dce4ef";
export const TEXT2 = "#4a6080";
export const GREEN = "#00897b";
export const AMBER = "#f59e0b";
export const RED   = "#e53935";
export const PURPLE= "#7c3aed";
export const BG    = "#f4f6f9";

// Shared card / input styles
export const card = {
  background: "#fff",
  borderRadius: 20,
  border: `1px solid ${BORDER}`,
  padding: 24,
  marginBottom: 10,
  boxShadow: "0 2px 12px rgba(10,22,40,.07)",
};
export const lbl = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: STEEL,
  display: "block",
  marginBottom: 8,
};
export const inp = {
  width: "100%",
  background: "#f6f9fc",
  border: `1.5px solid ${BORDER}`,
  borderRadius: 8,
  color: NAVY,
  fontSize: 14,
  padding: "11px 13px",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
export const btn = (active) => ({
  flex: 1,
  padding: "15px 20px",
  borderRadius: 13,
  border: "none",
  background: active
    ? "linear-gradient(135deg,#2d5bbf 0%,#4a8fe8 100%)"
    : "#b0bec5",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 0.5,
  cursor: active ? "pointer" : "not-allowed",
  boxShadow: active ? "0 4px 16px rgba(59,111,212,0.3)" : "none",
});

// Load tiers
export const LOAD_TIERS = [
  { key: "Small",  label: "Small Load",  cap: "About a quarter truck bed",              price: 75,  items: "A few trash bags, boxes, or loose household items.",                                         fill: 1,   bags: "1–5",   img: "/Load-Visuals/small.png", popular: true },
  { key: "Half",   label: "Medium Load", cap: "About half a truck bed",                price: 120, items: "Several bags, boxes, or loose items. Good for small cleanouts.",                            fill: 2,   bags: "6–15",  img: "/Load-Visuals/half.png" },
  { key: "ThreeQ", label: "Heavy Load",  cap: "About three quarters of a truck bed",   price: 175, items: "Large amount of bags, boxes, or yard debris. (No large furniture or appliances)",          fill: 3,   bags: "16–30", img: "/Load-Visuals/threequarter.png" },
  { key: "Full",   label: "PCS Cleanout", cap: "Full truck bed",                       price: 295, items: "Large cleanouts with many bags, boxes, loose items, and lightweight furniture.",           fill: 6,   bags: "30+",   img: "/Load-Visuals/full.png" },
];

// ── Full+ overage pricing (docs/pricing-overage-model.md) ──────────────────
// The four LOAD_TIERS stay untouched. Full covers up to FULL_BASE_ITEMS at the
// flat tier price; larger declared loads add a per-item rate by bracket, and
// the total booking price never exceeds PRICE_CEILING ("never more than $500").
// Server parity: create-checkout-session carries the same constants and math —
// change them together.
export const FULL_BASE_ITEMS = 40;
export const PRICE_CEILING = 500; // dollars, total gross cap
export const FULL_OVERAGE_BRACKETS = [
  { upTo: 50, perItem: 7 },
  { upTo: 60, perItem: 5 },
  { upTo: 80, perItem: 4 },
];

/**
 * Overage dollars for a declared Full-tier item count.
 * Returns 0 for null/invalid counts or counts within the Full base band.
 * Counts past the last bracket price at the ceiling: base + overage === $500.
 */
export function calcFullOverage(itemCount) {
  const fullPrice = LOAD_TIERS.find(t => t.key === "Full").price;
  const maxOverage = PRICE_CEILING - fullPrice;
  const n = Number(itemCount);
  if (!Number.isFinite(n) || n <= FULL_BASE_ITEMS) return 0;
  const lastBracket = FULL_OVERAGE_BRACKETS[FULL_OVERAGE_BRACKETS.length - 1].upTo;
  if (n > lastBracket) return maxOverage;
  let overage = 0;
  let prev = FULL_BASE_ITEMS;
  for (const b of FULL_OVERAGE_BRACKETS) {
    const items = Math.min(n, b.upTo) - prev;
    if (items > 0) overage += items * b.perItem;
    prev = b.upTo;
  }
  return Math.min(overage, maxOverage);
}

// Booking steps
export const STEPS = [
  { num: 1, title: "Upload Photos",   sub: "Photos of items for removal" },
  { num: 2, title: "Item Type",       sub: "What are you disposing?" },
  { num: 3, title: "Pickup Location", sub: "Address and driver notes" },
  { num: 4, title: "Load Size",       sub: "Review Your Load Size" },
  { num: 5, title: "When do you want this handled?",   sub: "We'll match your pickup based on your timing and route availability." },
  { num: 6, title: "Confirm & Pay",   sub: "Review and pay to confirm" },
];

// Payment methods
export const ZELLE_HANDLE = process.env.REACT_APP_ZELLE_HANDLE || "";

// First-haul launch offer — SINGLE source of truth for the display copy.
// Display-only in P2-B (sign-in banner); the Stripe promo mechanics are a
// separate, later branch (P2-C). Change the cap here, nowhere else.
export const FIRST_HAUL_OFFER = {
  // Master on/off switch for offer messaging (booking summary, banners). Flip to
  // false when the launch promo ends or the Stripe coupon's 10 redemptions are
  // used up, so no surface promises a free haul that checkout won't honor.
  active: true,
  anchorPrice: 75,             // struck-through reference price ($ Small tier)
  capCount: 10,                // matches the Stripe coupon max_redemptions (P2-C)
  capText: "first 10 hauls",   // cap copy — NOT rendered on the auth surface (owner ruling)
  // Eligibility limits (owner ruling 2026-07-04). Rendered wherever the offer
  // is promised; enforced at promo application in P2-C.
  exclusionsText: "Excludes large furniture and large appliances.",
  // Tiers the free first haul applies to (owner ruling 2026-07-07): Small + Medium
  // only. Keys are internal tier keys (Half = Medium). Heavy/Full excluded.
  // SERVER PARITY: create-checkout-session hardcodes the same ["Small","Half"]
  // set (edge functions cannot import this module) — change them together.
  eligibleTiers: ["Small", "Half"],
};

// Military non-affiliation disclaimer — must appear on every screen of the
// auth surface (v3 spec §6) and on legal/FAQ pages.
export const NON_AFFILIATION_DISCLAIMER = `${BRAND_NAME} Services is an independently owned and operated business. We are not affiliated with, endorsed by, or sponsored by the Department of Defense/Department of War, the U.S. Air Force, or any military installation, including Hill Air Force Base or Boyer Hill Military Housing.`;

export const PAYMENT_METHODS = [
  { key: "ApplePay",  color: "#000000", bg: "#f5f5f5", border: "#d1d5db", handle: "",  note: "Apple Pay, Google Pay, or saved wallet — instant confirmation" },
  { key: "SavedCard", color: "#635bff", bg: "#f0f0ff", border: "#a5b4fc", handle: "",  note: "Use a saved card on file" },
  { key: "Card",      color: "#635bff", bg: "#f0f0ff", border: "#a5b4fc", handle: "",  note: "Credit or debit card — instant confirmation" },
  { key: "Cash App",  color: "#00c244", bg: "#e8fff0", border: "#86efac", handle: process.env.REACT_APP_CASHAPP_HANDLE || "$LoadDashServices", note: "Send via Cash App" },
  { key: "Zelle",     color: "#6C1CD3", bg: "#f3e8ff", border: "#d8b4fe", handle: ZELLE_HANDLE, note: "Send via Zelle" },
];

// Pickup slots
export const PICKUP_SLOTS = [
  { day: "Monday",    time: "10:00 AM – 1:00 PM" },
  { day: "Tuesday",   time: "10:00 AM – 1:00 PM" },
  { day: "Wednesday", time: "10:00 AM – 1:00 PM" },
  { day: "Thursday",  time: "10:00 AM – 1:00 PM" },
  { day: "Friday",    time: "10:00 AM – 1:00 PM" },
  { day: "Saturday",  time: "10:00 AM – 2:00 PM" },
];

// Max jobs per zone per day (legacy — kept for backward compatibility)
export const ZONE_CAP = 8;

// LEU capacity. Canonical numbers live in src/leu/canonical.js.
// LOAD_TIERS[].fill is the LEU value per tier (Small=1, Half=2, ThreeQ=3, Full=6)
// Item type LEU reference: Cardboard = 15 flat broken-down boxes per 1 LEU

// Re-exported from the canonical module so there is one definition (value: 18).
export { ROUTE_LEU_BUDGET };

/**
 * @deprecated use ROUTE_LEU_BUDGET (src/leu/canonical.js).
 * Backward-compatible alias kept for existing importers; same value (18).
 * Route planning scale, not physical bed capacity.
 */
export const TRUCK_MAX_LEU = ROUTE_LEU_BUDGET;

/**
 * @deprecated Invalid as a physical per-run truck capacity because the Full
 * tier is 6 LEU. Do not use for fit checks. Use vehicle bed LEU instead
 * (src/dispatch/truckCapacityPolicy.js: TIER_MIN_VEHICLE_LEU / max_leu).
 * Value left unchanged to preserve behavior for any existing reader.
 */
export const TRUCK_RUN_CAPACITY_LEU = 3;

export const ZONE_MAX_LEU  = 24;    // Max LEU per zone per day (8 jobs × avg 3 LEU)
export const DAY_MAX_LEU   = 48;    // Max LEU across all zones per day

/** Get LEU for a load size key. Single derivation point from LOAD_TIERS.fill */
export function getLEU(sizeKey) {
  return LOAD_TIERS.find(t => t.key === sizeKey)?.fill || 1;
}

// Customer-facing tier display labels.
// Storage/runtime keys are UNCHANGED — Half stays the medium-load key, ThreeQ
// stays the heavy-load key. This map only controls what a customer reads.
// Customers must never see "Half" or "ThreeQ".
const CUSTOMER_TIER_LABELS = {
  Small:  "Small",
  Half:   "Medium",
  ThreeQ: "Heavy",
  Full:   "Full",
};

/**
 * Customer-facing label for an internal size key (Small/Half/ThreeQ/Full).
 * Fail closed on an unrecognized key — returns null so callers render a neutral
 * placeholder ("—") rather than mislabeling an unknown tier as "Small".
 * @param {string} sizeKey internal storage key
 * @returns {string|null}
 */
export function getCustomerTierLabel(sizeKey) {
  return CUSTOMER_TIER_LABELS[sizeKey] || null;
}

// ── Model A Earnings Split Constants ──────────────────────
export const COMPANY_MARGIN_PCT = 0.10;   // 10% company margin on gross
export const STRIPE_FEE_PCT    = 0.035;   // 3.5% Stripe processing fee

// Quick Pickup surcharge split (owner ruling 2026-07-07).
// The customer pays QUICK_SURCHARGE for priority scheduling. On a Quick job
// that surcharge splits three ways: the company keeps a flat QUICK_COMPANY_CUT,
// Stripe takes its processing fee on the surcharge, and the Hauler keeps the
// remainder (~$13.33). Non-quick jobs are unaffected. Payout math lives in
// marketConfig.js calcSplit(); the $19 charge itself is applied server-side in
// create-checkout-session (QUICK_SURCHARGE_CENTS).
export const QUICK_SURCHARGE    = 19;     // dollars — customer-facing rush fee
export const QUICK_COMPANY_CUT  = 5;      // dollars — flat platform cut of the surcharge

// Mattress disposal handling surcharge (owner ruling 2026-07-10).
// Mattresses need special handling at the disposal facility, so any booking
// whose selected item types include "Mattress" adds a flat MATTRESS_SURCHARGE
// to the customer total. This is a disposal-cost pass-through: it is added to
// the Stripe charge only and is deliberately NOT part of the tier gross used
// for Hauler pay / viability. Applied server-side in create-checkout-session
// (MATTRESS_SURCHARGE_CENTS); the client mirrors it for display parity only.
export const MATTRESS_SURCHARGE = 15;     // dollars — customer-facing mattress handling fee
// DUMP_FEE and FUEL_RATE_PER_MILE removed — use MARKETS[marketId] in
// src/utils/marketConfig.js as the single source of truth for cost inputs.

// Zone definitions — SINGLE SOURCE OF TRUTH for street→zone mapping.
// zoneService partial fallbacks, the Step 5 zone-card street display, and the
// CLAUDE.md zone table all derive from / mirror this block. If a street moves
// zones, change it here (streets + keywords) and nowhere else.
export const ZONES = [
  {
    key: "A", label: "Zone A — North", color: "#1565c0", bg: "#e3f2fd", avgMileage: 4.2,
    streets: "Appomattox Cir, Bull Run Cir, Gettysburg Cir, Grant Cir, Jamestown Cir, Lincoln Cir, N Church Cir, N Liberty Way, Richmond Cir, Shenandoah Cir, Truman Cir, Washington Cir, Washington St",
    keywords: ["appomattox","appomattox cir","appomattox circle","bull run","bull run cir","bull run circle","gettysburg","gettysburg cir","gettysburg circle","grant cir","grant circle","jamestown","jamestown cir","jamestown circle","lincoln cir","lincoln circle","n church","n church cir","north church cir","north church circle","n liberty","north liberty","richmond cir","richmond circle","shenandoah","shenandoah cir","shenandoah circle","truman cir","truman circle","washington cir","washington circle","washington st","washington street"],
  },
  {
    key: "B", label: "Zone B — Central", color: "#00897b", bg: "#e0f2f1", avgMileage: 3.1,
    streets: "6th St, Bedford Cir, Braddock, Charleston St, Charleston Cir, Charlestown Lp, Hamilton Dr, N Bridge Cir, Princeton Dr, Trenton Cir",
    keywords: ["6th st","6th street","bedford cir","bedford circle","braddock","charleston cir","charleston circle","charleston st","charleston street","charlestown loop","charlestown lp","charlestown","hamilton dr","hamilton drive","hamilton st","hamilton street","hamilton","n bridge","n bridge cir","north bridge cir","north bridge circle","princeton dr","princeton drive","princeton st","princeton street","princeton","trenton cir","trenton circle"],
  },
  {
    key: "C", label: "Zone C — Southwest", color: "#6d28d9", bg: "#ede9fe", avgMileage: 5.8,
    streets: "Adams Cir, Airlane Dr, Arsenal Rd, Cambridge Lp, Cambridge St, E 200 S, E 225 S, Hancock Cir, Harrington Lp, Roxbury Cir",
    keywords: ["airlane dr","airlane drive","s airlane","harrington","harrington loop","harrington lp","cambridge lp","cambridge loop","roxbury","roxbury cir","roxbury circle","adams cir","adams circle","hancock","hancock cir","hancock circle","e 200 s","e 225 s","arsenal","cambridge ln","cambridge lane","cambridge st","cambridge street"],
  },
  {
    key: "D", label: "Zone D — Southeast", color: "#c2410c", bg: "#fff7ed", avgMileage: 6.4,
    streets: "Colony Lp, Concord Wy, Congress Wy, Constitution Wy, Franklin Wy, Freedom Cir, Hale Wy, Independence Ln, Lexington Wy, Liberty Wy, Minuteman Wy, Revere Cir, Saratoga Wy, Yorktown Wy",
    keywords: ["colony loop","colony lp","colony cir","colony circle","colony","concord wy","concord way","concord st","concord street","concord","congress wy","congress way","congress st","congress street","congress","constitution wy","constitution way","constitution rd","constitution road","constitution","e 600 s","east 600 south","e 700 s","franklin wy","franklin way","franklin st","franklin street","franklin","freedom cir","freedom circle","freedom","hale wy","hale way","hale","independence ln","independence lane","independence st","independence street","independence","lexington wy","lexington way","lexington st","lexington street","lexington","liberty wy","liberty way","liberty st","liberty street","minuteman wy","minuteman way","minuteman st","minuteman street","minuteman","revere cir","revere circle","revere","saratoga wy","saratoga way","saratoga st","saratoga street","saratoga","yorktown wy","yorktown way","yorktown st","yorktown street","yorktown"],
  },
];

// Zone polygons for coordinate-based detection
export const ZONE_POLYGONS = {
  A: [
    [41.12951,-112.00333],[41.12819,-112.00163],[41.12521,-112.00114],
    [41.12173,-112.00235],[41.11975,-112.00138],[41.11793,-111.99822],
    [41.11500,-111.99700],[41.11500,-111.97500],[41.11500,-111.96000],
    [41.11809,-111.95886],[41.11975,-111.96080],[41.12173,-111.96323],
    [41.12372,-111.96566],[41.12554,-111.96809],[41.12703,-111.97198],
    [41.12852,-111.97684],[41.12951,-111.98170],[41.13017,-111.98753],
    [41.13050,-111.99385],[41.13001,-111.99968],[41.12951,-112.00163],
  ],
  D: [
    [41.11500,-111.99700],[41.11412,-111.99725],[41.11147,-111.99482],
    [41.10983,-111.99288],[41.10783,-111.99045],[41.10518,-111.98850],
    [41.10253,-111.98777],[41.10088,-111.98559],[41.09989,-111.98267],
    [41.09939,-111.97976],[41.09922,-111.97684],[41.09939,-111.97295],
    [41.09989,-111.96906],[41.10055,-111.96518],[41.10138,-111.96129],
    [41.10220,-111.95691],[41.10320,-111.95351],[41.10452,-111.95157],
    [41.10651,-111.95060],[41.10849,-111.95108],[41.11048,-111.95254],
    [41.11246,-111.95448],[41.11445,-111.95546],[41.11644,-111.95691],
    [41.11500,-111.96000],[41.11500,-111.97500],
  ],
};

// Admin phone for misrep alerts (set REACT_APP_ADMIN_PHONE in .env.local)
export const ADMIN_PHONE = process.env.REACT_APP_ADMIN_PHONE || "";

// Mapbox token (from env)
export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const BRAND_COLORS = {
  NAVY:   '#0A1628',
  NAVY2:  '#012340',
  BLUE:   '#3D90D9',
  GRAY:   '#F2F2F2',
  ORANGE: '#F29727',
  WHITE:  '#FFFFFF',
};
